import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Copy, Loader2, PlugZap, RefreshCw, ServerCog, XCircle, Activity, Wrench, FolderOpen, GitCompare, PackageX, KeyRound, Monitor, Unplug, Download, ExternalLink, PackageCheck, AlertTriangle } from 'lucide-react';
import type { TiaReadiness, TiaText } from '@realplc/types';
import { useTranslation } from '../i18n';
import {
  createRealPlcAgentPairingCode,
  readTiaReadiness,
  runTiaAgentSnapshot,
  type AgentStatusResponse,
  type OpennessAgentRecord,
  type PairingCodeResponse,
} from '../services/opennessAgent';
import { useTiaAgentStatusStore } from '../stores/tiaAgentStatusStore';
import { useSessionTiaTargetStore } from '../stores/sessionTiaTargetStore';
import {
  loadTiaAgentRelease,
  resolveTiaAgentDownloadUrl,
  resolveTiaAgentUpdateState,
  type TiaAgentReleaseInfo,
} from '../services/tiaAgentRelease';

interface TiaAgentSettingsDialogProps {
  isOpen: boolean;
  projectId?: string | null;
  onClose: () => void;
  onStatusChange?: (status: AgentStatusResponse | null) => void;
}

type CheckState = 'idle' | 'checking' | 'success' | 'error';

type RefreshStatusOptions = {
  clearMessage?: boolean;
  showError?: boolean;
};

function formatTime(value?: number | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function readinessIcon(state: TiaReadiness): React.ReactNode {
  switch (state) {
    case 'ready':
      return <CheckCircle2 size={15} className="text-emerald-600" />;
    case 'busy':
      return <Activity size={15} className="text-blue-600" />;
    case 'checking':
      return <Loader2 size={15} className="animate-spin text-zinc-500" />;
    case 'offline':
      return <Unplug size={15} className="text-red-600" />;
    case 'no_project_selected':
    case 'project_not_found':
    case 'upgrade_required':
      return <FolderOpen size={15} className="text-amber-600" />;
    case 'project_mismatch':
      return <GitCompare size={15} className="text-orange-600" />;
    case 'capability_missing':
      return <PackageX size={15} className="text-purple-600" />;
    case 'auth_required':
      return <KeyRound size={15} className="text-red-600" />;
    case 'tia_not_running':
      return <Monitor size={15} className="text-amber-600" />;
    default:
      return <Wrench size={15} className="text-amber-600" />;
  }
}

function flattenCapabilities(agent?: OpennessAgentRecord): string[] {
  const value = agent?.capabilities;
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
  const list = (value as { actions?: unknown; capabilities?: unknown }).actions
    ?? (value as { capabilities?: unknown }).capabilities;
  if (Array.isArray(list)) return list.filter((item): item is string => typeof item === 'string');
  return Object.entries(value)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key);
}

export const TiaAgentSettingsDialog: React.FC<TiaAgentSettingsDialogProps> = ({
  isOpen,
  projectId,
  onClose,
  onStatusChange,
}) => {
  const { t, language } = useTranslation();
  const [pairing, setPairing] = useState<PairingCodeResponse | null>(null);
  const status = useTiaAgentStatusStore((state) => state.status);
  const sessionTiaProjectRef = useSessionTiaTargetStore((state) => state.tiaProjectRef);
  const statusLoadState = useTiaAgentStatusStore((state) => state.loadState);
  const statusError = useTiaAgentStatusStore((state) => state.error);
  const refreshGlobalStatus = useTiaAgentStatusStore((state) => state.refresh);
  const startGlobalStatus = useTiaAgentStatusStore((state) => state.start);
  const [isCreatingCode, setIsCreatingCode] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [checkState, setCheckState] = useState<CheckState>('idle');
  const [message, setMessage] = useState('');
  const [release, setRelease] = useState<TiaAgentReleaseInfo | null>(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError] = useState('');

  // The readiness the TIA button and the post-generation card show too
  // (readTiaReadiness), in the readiness table's words, for the conversation's project.
  const readiness = useMemo(
    () => readTiaReadiness(status, projectId, sessionTiaProjectRef),
    [status, projectId, sessionTiaProjectRef],
  );
  const say = (text: TiaText) => (language === 'Chinese' ? text.zh : text.en);
  // The Agent that readiness is about, so the name and version beside it
  // describe the same machine; any online Agent when none is usable.
  const onlineAgent = useMemo(
    () => status?.agents?.find((agent) => agent.id === readiness.agentId && agent.status === 'online')
      ?? status?.agents?.find((agent) => agent.status === 'online')
      ?? null,
    [readiness.agentId, status],
  );
  const knownAgent = useMemo(
    () => onlineAgent ?? status?.agents?.find((agent) => Boolean(agent.agentVersion)) ?? null,
    [onlineAgent, status],
  );
  const capabilities = useMemo(() => flattenCapabilities(onlineAgent ?? undefined), [onlineAgent]);
  const connectionState = status?.connectionState ?? status?.status;
  // A busy Agent counts: its validation queues behind the running job.
  const isReady = readiness.canValidate;
  const updateState = resolveTiaAgentUpdateState(knownAgent?.agentVersion, release);
  const downloadUrl = resolveTiaAgentDownloadUrl(release);
  const readinessUnknown = !status && statusLoadState === 'detecting';

  const refreshStatus = async (options: RefreshStatusOptions = {}) => {
    const { clearMessage = true, showError = true } = options;
    setIsLoadingStatus(true);
    if (clearMessage) setMessage('');
    try {
      await refreshGlobalStatus(projectId);
    } catch (error) {
      if (showError) {
        setMessage(error instanceof Error ? error.message : t('Failed to load RealPLC Agent status'));
      }
    } finally {
      setIsLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const stop = startGlobalStatus();
    void refreshGlobalStatus(projectId);
    return stop;
  }, [isOpen, projectId, refreshGlobalStatus, startGlobalStatus]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setReleaseLoading(true);
    setReleaseError('');
    void loadTiaAgentRelease()
      .then((nextRelease) => {
        if (!cancelled) setRelease(nextRelease);
      })
      .catch((error) => {
        if (!cancelled) {
          setRelease(null);
          setReleaseError(error instanceof Error
            ? error.message
            : t('Failed to load RealPLC Agent release information'));
        }
      })
      .finally(() => {
        if (!cancelled) setReleaseLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, t]);

  useEffect(() => {
    onStatusChange?.(status);
  }, [onStatusChange, status]);

  if (!isOpen) return null;

  const createCode = async () => {
    setIsCreatingCode(true);
    setMessage('');
    try {
      const next = await createRealPlcAgentPairingCode(projectId);
      setPairing(next);
      setMessage(t('Pairing code created. Enter it in the local RealPLC Agent.'));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('Failed to create pairing code'));
    } finally {
      setIsCreatingCode(false);
    }
  };

  const runCheck = async () => {
    setCheckState('checking');
    setMessage('');
    try {
      await runTiaAgentSnapshot(projectId);
      setCheckState('success');
      setMessage(t('Configuration successful. SCL closed-loop validation is available.'));
      await refreshStatus({ clearMessage: false, showError: false });
    } catch (error) {
      setCheckState('error');
      setMessage(error instanceof Error ? error.message : t('TIA Connector environment check failed'));
    }
  };

  const copyCode = async () => {
    if (!pairing?.code) return;
    await navigator.clipboard?.writeText(pairing.code);
    setMessage(t('Pairing code copied.'));
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-[#101418]">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300">
              <PlugZap size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{t('TIA Connector')}</h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('Pair, detect, and prepare Siemens SCL validation.')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            aria-label={t('Close')}
          >
            <XCircle size={18} />
          </button>
        </div>

        <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {!projectId ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              {t('The Agent connection is available in this conversation, but SCL validation needs an explicitly bound project. Open a project conversation to validate. The target is always shown on the picker above the input box, and you can change it.')}
            </div>
          ) : null}
          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {updateState === 'required'
                    ? <AlertTriangle size={16} className="text-red-600" />
                    : updateState === 'current'
                      ? <PackageCheck size={16} className="text-emerald-600" />
                      : <Download size={16} className="text-cyan-700 dark:text-cyan-300" />}
                  <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Install and update')}</h4>
                </div>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                  {releaseLoading
                    ? t('Checking the latest RealPLC Agent release...')
                    : updateState === 'required'
                      ? t('This Agent version is no longer supported. Update before continuing.')
                      : updateState === 'available'
                        ? t('A newer RealPLC Agent version is available.')
                        : updateState === 'current'
                          ? t('The installed RealPLC Agent is up to date.')
                          : updateState === 'install'
                            ? t('Download the Windows x64 installer, then return here to pair the Agent.')
                            : t('The download will appear after a release source is configured.')}
                </p>
                <div className="mt-3 grid gap-2 text-xs text-zinc-500 dark:text-zinc-400 sm:grid-cols-2">
                  <div>{t('Installed version')}: <span className="font-medium text-zinc-800 dark:text-zinc-200">{knownAgent?.agentVersion || '-'}</span></div>
                  <div>{t('Latest version')}: <span className="font-medium text-zinc-800 dark:text-zinc-200">{release?.version || '-'}</span></div>
                </div>
                {releaseError ? <div className="mt-2 text-xs text-red-600 dark:text-red-400">{releaseError}</div> : null}
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                {downloadUrl ? (
                  <a
                    href={downloadUrl}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors ${
                      updateState === 'required'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-cyan-600 hover:bg-cyan-700'
                    }`}
                  >
                    <Download size={15} />
                    {updateState === 'required'
                      ? t('Update required')
                      : updateState === 'available' && release?.version
                        ? `${t('Update to')} ${release.version}`
                        : t('Download and install')}
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-zinc-300 px-3 py-2 text-sm font-medium text-zinc-600 opacity-70 dark:bg-zinc-700 dark:text-zinc-300"
                  >
                    {releaseLoading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                    {releaseLoading ? t('Checking release') : t('Download unavailable')}
                  </button>
                )}
                <>
                  <a
                    href={release?.releasePageUrl || 'https://github.com/JadeHuang/RealPLC-Agent/releases/latest'}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-cyan-700 dark:text-zinc-400 dark:hover:text-cyan-300"
                  >
                    {!release || release.source === 'cloud'
                      ? language === 'Chinese' ? 'GitHub 备用下载' : 'GitHub backup download'
                      : t('View release notes')} <ExternalLink size={12} />
                  </a>
                </>
              </div>
            </div>
          </section>
          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Pair RealPLC Agent Core')}</h4>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Create a short-lived code and enter it in the local CloudBridge agent.')}</p>
              </div>
              <button
                type="button"
                onClick={createCode}
                disabled={isCreatingCode}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-cyan-200 bg-white px-3 py-2 text-sm font-medium text-cyan-700 transition-colors hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200 dark:hover:bg-cyan-500/20"
              >
                {isCreatingCode ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
                {t('Create Pairing Code')}
              </button>
            </div>
            {pairing ? (
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-dashed border-cyan-300 bg-white p-3 dark:border-cyan-500/30 dark:bg-[#0d1117] sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{t('Pairing Code')}</div>
                  <div className="mt-1 font-mono text-2xl font-semibold tracking-[0.2em] text-zinc-900 dark:text-zinc-100">{pairing.code}</div>
                  <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Expires')}: {formatTime(pairing.expiresAt)}</div>
                </div>
                <button
                  type="button"
                  onClick={copyCode}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  <Copy size={15} />
                  {t('Copy')}
                </button>
              </div>
            ) : null}
          </section>

          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Agent Status')}</h4>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Connection is organization-wide; readiness is evaluated for the current project.')}</p>
              </div>
              <button
                type="button"
                onClick={() => void refreshStatus()}
                disabled={isLoadingStatus}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                {isLoadingStatus ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                {t('Refresh')}
              </button>
            </div>

            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Connection')}</div>
                <div className={`mt-1 flex items-center gap-2 font-semibold ${connectionState === 'online' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {connectionState === 'online' ? <CheckCircle2 size={15} /> : statusLoadState === 'detecting' ? <Loader2 size={15} className="animate-spin" /> : <Unplug size={15} />}
                  {connectionState === 'online' ? t('Online') : statusLoadState === 'detecting' ? t('Detecting') : t('Offline')}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Agent')}</div>
                <div className="mt-1 truncate font-semibold text-zinc-900 dark:text-zinc-100">{onlineAgent?.agentName || '-'}</div>
                <div className="mt-1 truncate text-xs text-zinc-500 dark:text-zinc-400">{onlineAgent?.agentVersion || '-'}</div>
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-[#0d1117]">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">{t('Readiness')}</div>
              <div className="mt-1 flex items-center gap-2 font-semibold text-zinc-900 dark:text-zinc-100">
                {readinessUnknown
                  ? <Loader2 size={15} className="animate-spin text-zinc-500" />
                  : readiness.noProject
                    ? <FolderOpen size={15} className="text-amber-600" />
                    : readinessIcon(readiness.state)}
                {readinessUnknown ? t('Detecting') : say(readiness.label)}
              </div>
              {readinessUnknown ? null : (
                <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{say(readiness.explanation)}</div>
              )}
              {statusLoadState === 'stale' ? <div className="mt-1 text-xs text-zinc-500">{t('Live status unavailable; showing last known state.')}</div> : null}
              {statusLoadState === 'error' && statusError ? <div className="mt-1 text-xs text-red-600">{statusError}</div> : null}
            </div>

            {capabilities.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {capabilities.slice(0, 10).map((capability) => (
                  <span key={capability} className="rounded-full border border-cyan-200 bg-cyan-50 px-2 py-1 text-xs text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-200">
                    {capability}
                  </span>
                ))}
              </div>
            ) : null}
          </section>

          <section className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-[#151b22]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('Environment Check')}</h4>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('Runs a read-only snapshot check before SCL validation is enabled.')}</p>
              </div>
              <button
                type="button"
                onClick={runCheck}
                disabled={checkState === 'checking' || connectionState !== 'online' || !projectId}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {checkState === 'checking' ? <Loader2 size={15} className="animate-spin" /> : <ServerCog size={15} />}
                {t('Check Environment')}
              </button>
            </div>
            <div className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
              isReady
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                : checkState === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
                  : 'border-zinc-200 bg-white text-zinc-600 dark:border-zinc-800 dark:bg-[#0d1117] dark:text-zinc-300'
            }`}>
              {message || (connectionState === 'online'
                ? projectId
                  ? t('Agent is connected. Run the environment check to finish setup.')
                  : t('Agent is Online. Select a project conversation before running validation.')
                : t('Waiting for local RealPLC Agent connection.'))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
